/**
 * 新建项目的三阶段与「会话没建成」那一屏（Z11 / Z12）。
 *
 * 这一份的核心是一句设计稿的话：**项目是资产，会话只是一次对话。**
 * 所以会话建不成时**不整个回滚**——把刚建好的仓库和初始提交删掉，
 * 比留着一个没有会话的项目糟得多，而后者用户自己再建一次就有了。
 */

/** 三阶段，顺序固定。会话排最后：仓库没好就建会话，得到的是一个不能派活的会话。 */
export const INIT_STAGES = Object.freeze(['project', 'repo', 'conversation'] as const)

export type InitStage = (typeof INIT_STAGES)[number]

/**
 * 一步的状态。**没有百分比**——三步完成两步不显示 66%。
 *
 * `none` 是 2026-08-30 加的第五档，它说的是**这一步不属于这个项目**，
 * 既不是「做完了」也不是「没做成」。
 *
 * 加它的理由不是分类学，是一个真实的产品故障：`POST /projects` 从来不建
 * 仓库（`NewProject` 里根本没有仓库这个字段），所以从启动器新建的每一个
 * 项目，服务端报的都是 `repository: 'none'`，而上一版把它译成了 `fail`——
 * **每一个新建项目都显示「第 2 步失败」**。
 *
 * 拿现成的四档随便挑一档顶上，四种都是撒谎：
 *
 *   - `fail` 说「坏了，去重试」——没有可重试的东西
 *   - `wait` 说「等一下就好」——永远不会好
 *   - `running` 说「正在建」——没有任何东西在跑
 *   - `done` 说「仓库建好了」——没有仓库
 *
 * 判据是 CLAUDE.md 那条：**两件事如果要求用户做的下一个动作不同，
 * 就不许合成一种说法。** 「没有仓库」的下一个动作是「什么都不用做」，
 * 「建仓库失败」的下一个动作是「重试」。
 */
export type StageState = 'done' | 'running' | 'wait' | 'fail' | 'none'

export interface StageRow {
  readonly stage: InitStage
  readonly state: StageState
  /**
   * 没走到的步骤要写明为什么。设计稿：不能只画一个灰点让人猜。
   * 走到了的步骤这里是 `null`。
   */
  readonly whyNotYet: 'lulu_workbench.init.blocked_by_prev' | null
}

/**
 * 由三步各自的结果铺出这一页要画的行。
 *
 * **前一步没过，后面一律 `wait` 且带上原因**，不是 `fail`：
 * 它们没有失败，它们根本没开始。把它们画成失败，用户会以为
 * 三件事都坏了，而其实只坏了一件。
 *
 * **只有 `fail` 会挡住后面。** `none` 不挡：那一步不属于这个项目，
 * 后面那些照样能做完（一个没有仓库的项目照样开得了会话——事实上
 * 从启动器建出来的每一个都是这样）。把 none 也当成挡路的，
 * 会让「建会话」那一格永远显示「上一步没过」，而上一步没有没过。
 */
export function stageRows(results: Readonly<Record<InitStage, StageState>>): readonly StageRow[] {
  let blocked = false
  return INIT_STAGES.map((stage) => {
    if (blocked) return { stage, state: 'wait' as const, whyNotYet: 'lulu_workbench.init.blocked_by_prev' as const }
    const state = results[stage]
    if (state === 'fail') blocked = true
    return { stage, state, whyNotYet: null }
  })
}

/**
 * 这一页能不能说「已准备好」。
 *
 * **每一步都得有个准信才行。** running / wait / fail 任一都停在这里——
 * 设计稿那句「这一页永远不显示『已准备好』」说的就是：
 * 它只在全部确认之后切走，而不是在这一页上变出一行绿字。
 *
 * `none` 放行，因为它**是一个准信**：我们知道这个项目没有仓库。
 * 这不是「往下凑」（把不知道说成知道），恰好相反——它是我们知道的那件事。
 * 真正没准信的那两档（wait / running）与坏了的那档（fail）照旧一票否决。
 *
 * 这条规则必须与服务端 `initStageView` 的 ready 逐字同义（服务端那边是
 * 「非归档 && 每一格都不是 pending」）。两份真相分家的那天没有任何东西
 * 会红，所以 `provisioning-init.test.ts` 最后一组把两者钉在一起比。
 */
export function isReady(results: Readonly<Record<InitStage, StageState>>): boolean {
  return INIT_STAGES.every((s) => results[s] === 'done' || results[s] === 'none')
}

/**
 * 重试要补哪一步。
 *
 * 回第一个没成的那一步。**已经 done 的绝不重做**——
 * 重做会用掉一把新钥匙，而那正是「又建了一个项目」的来路。
 */
export function retryFrom(results: Readonly<Record<InitStage, StageState>>): InitStage | null {
  return INIT_STAGES.find((s) => results[s] !== 'done') ?? null
}

/**
 * 会话建不成之后，项目要不要回滚。
 *
 * **恒为 false，而且这个函数没有入参。**
 *
 * 写成一个不收参数的函数是刻意的：收了参数，就有人会传进来一个
 * 「这次比较特殊」，而那一次会把用户刚建好的仓库删掉。设计稿把这条
 * 单独列出来，正因为它最容易被实现成「整个回滚」。
 */
export function shouldRollbackProject(): false {
  return false
}

/**
 * 三步共用的幂等键。
 *
 * **同一把钥匙贯穿三步**，重试时原样带回去——所以重复点重试不会
 * 建出第二个项目、第二个仓库、第二条会话。
 *
 * 键由调用方在**第一次**发起时生成并存住；这里只负责派生每一步的
 * 子键，且派生是纯函数——中间任何一环随机，整条链就等于没有
 * （CLAUDE.md §8 那条「派生链必须一路算到底」）。
 */
export function stageIdempotencyKey(rootKey: string, stage: InitStage): string {
  return `${rootKey}:${stage}`
}

// ── 接真实 GET /projects/:id/init-status ───────────────────────────
//
// 后端把三阶段全部从真实行推（项目行 / repos 行 / project_environments 行），
// openapi 那段描述逐字写着「没有任何一个阶段是编的」。所以这里**只做翻译，
// 不做推断**——前端一旦开始推断，就会在后端说 pending 的时候说「已准备好」。

/** 服务端给的四个阶段。词表与我们的 StageState 不同，所以要显式翻译。 */
export interface WireInitStatus {
  readonly stages: {
    readonly project: 'ready'
    readonly repository: 'ready' | 'none'
    readonly environments: 'ready' | 'pending'
    /**
     * 会话那一格。**2026-08-30 加的，界面第三格读的就是它。**
     *
     * 在它之前这一格读的是 `environments`——而环境两行是插项目时触发器
     * 种下的，它 ready 的那一刻一条会话都还没有。于是「建会话」那一格会在
     * 零条会话的时候显示「好了」。**报的和画的不是同一件事。**
     */
    readonly conversation: 'ready' | 'pending'
  }
  readonly ready: boolean
}

/**
 * 这份响应读得懂吗。
 *
 * 服务端比 console 旧一拍时（两者是各自的镜像，同一次发布里也可能差几十秒）
 * `conversation` 会缺席。缺席时**不兜底**：兜成 pending 会让那一格永远
 * 显示「等着」，兜成 ready 会在零条会话时说好了——两个方向都是编。
 *
 * 判成读不到，调用方按既有规矩整块不渲染（launch.tsx 那句「拿不到就整块
 * 不渲染：一个编出来的阶段列表比不显示糟得多」）。
 */
export function isReadableInitStatus(wire: unknown): wire is WireInitStatus {
  if (typeof wire !== 'object' || wire === null) return false
  const stages = (wire as { stages?: unknown }).stages
  if (typeof stages !== 'object' || stages === null) return false
  const s = stages as Record<string, unknown>
  return (
    s.project === 'ready' &&
    (s.repository === 'ready' || s.repository === 'none') &&
    (s.environments === 'ready' || s.environments === 'pending') &&
    (s.conversation === 'ready' || s.conversation === 'pending') &&
    typeof (wire as { ready?: unknown }).ready === 'boolean'
  )
}

/**
 * 把服务端的词翻成界面的档。
 *
 * 三处翻译各有理由，都不是随手对的：
 *
 *   - `repository: 'none'` → `none`。**不是 fail，也不是 wait。**
 *     它说的是「这个项目没有远程仓库」，一件已知的事。上一版译成 fail，
 *     而从启动器建出来的项目 100% 是这一档——于是每一个新建项目都显示
 *     「第 2 步失败」。见 StageState 上那段。
 *   - `conversation` 读的是 `conversation`，不是 `environments`。
 *     但环境没齐时会话那一格也不许说好了：环境是会话的前置，
 *     环境没齐而会话行已经在，那是一个我们解释不了的状态，
 *     照实降级成「还没到」比顺着它说「好了」诚实。
 *   - `project` 只有 'ready' 一个取值：项目行不存在时整个接口 404，
 *     所以走到这里它必然成了。
 */
export function fromWireInitStatus(
  wire: WireInitStatus,
): Readonly<Record<InitStage, StageState>> {
  const conversationDone =
    wire.stages.conversation === 'ready' && wire.stages.environments === 'ready'
  return {
    project: 'done',
    repo: wire.stages.repository === 'ready' ? 'done' : 'none',
    conversation: conversationDone ? 'done' : 'wait',
  }
}

/**
 * 关页重进之后该显示哪一步。
 *
 * **判据只用服务端给的 `ready`，不用我们自己算的那个。**
 *
 * 两者今天应该一致，而一旦不一致，可信的是服务端那个——它看的是真实行，
 * 我们看的是翻译之后的投影。用自己算的那个，等于给「界面说好了、
 * 后端说还没好」留了一条路，而那条路上用户会去派活，然后发现派不动。
 */
export function resumedStage(wire: WireInitStatus): {
  readonly rows: readonly StageRow[]
  readonly ready: boolean
} {
  return { rows: stageRows(fromWireInitStatus(wire)), ready: wire.ready }
}
